import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ApiCatalogListResponse,
  ApiCatalogDetailResponse,
  ApiCatalogOccurrenceResponse,
  ApiCatalogDomainMetadataResponse,
  ApiCatalogCredentialsResponse,
  ApiCatalogCreateRequest,
  ApiCatalogUpdateRequest,
  ApiCatalogMutationResponse,
  ApiTestRequest,
  ApiTestResponse,
  ApiTestErrorResponse,
  ApiTestUrlRequest,
  ApiTestUrlResponse,
  ApiCatalogSourcePreviewResponse,
  ApiCatalogSourceCreateRequest,
  ApiCatalogSourceCreateResponse,
  ApiCatalogSourceListResponse,
  ApiCatalogSourceRefreshResponse,
  ApiCatalogSource,
} from '@chatsdet/types';
import { API_URL } from '../../config';
import { ApiError, toApiError } from '../../lib/api-error';

export type ApiRequestListQuery = {
  page?: number;
  pageSize?: number;
  search?: string;
  method?: string;
  methods?: string[];
  propertyId?: string | null;
  domains?: string[];
  status?: '2xx' | '4xx' | '5xx' | null;
  statuses?: Array<'2xx' | '4xx' | '5xx'>;
  credentialIds?: string[];
  liveness?: 'live' | 'obsolete' | null;
  lastSeenStart?: string | null;
  lastSeenEnd?: string | null;
};

const buildQueryString = (params: ApiRequestListQuery): string => {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.search) searchParams.set('search', params.search);
  if (params.method) searchParams.set('method', params.method);
  if (params.methods && params.methods.length > 0) {
    for (const method of params.methods) {
      searchParams.append('methods', method);
    }
  }
  if (params.propertyId) searchParams.set('propertyId', params.propertyId);
  if (params.domains && params.domains.length > 0) {
    for (const domain of params.domains) {
      searchParams.append('domains', domain);
    }
  }
  if (params.status) searchParams.set('status', params.status);
  if (params.statuses && params.statuses.length > 0) {
    for (const status of params.statuses) {
      searchParams.append('statuses', status);
    }
  }
  if (params.credentialIds && params.credentialIds.length > 0) {
    for (const credentialId of params.credentialIds) {
      searchParams.append('credentialIds', credentialId);
    }
  }
  if (params.liveness) searchParams.set('liveness', params.liveness);
  if (params.lastSeenStart) searchParams.set('lastSeenStart', params.lastSeenStart);
  if (params.lastSeenEnd) searchParams.set('lastSeenEnd', params.lastSeenEnd);
  return searchParams.toString();
};

const fetchApiRequestList = async (
  params: ApiRequestListQuery
): Promise<ApiCatalogListResponse> => {
  const query = buildQueryString(params);
  const response = await fetch(`${API_URL}/api-requests${query ? `?${query}` : ''}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to load API requests: ${response.status}`);
  }

  const body = (await response.json()) as ApiCatalogListResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to load API requests');
  }
  return body;
};

export const fetchApiRequestDetail = async (
  requestId: string,
  options?: {
    apiUrl?: string;
    fetchFn?: typeof fetch;
  }
): Promise<ApiCatalogDetailResponse> => {
  const apiUrl = options?.apiUrl ?? API_URL;
  const fetchFn = options?.fetchFn ?? fetch;
  const response = await fetchFn(`${apiUrl}/api-requests/${requestId}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to load API request detail');
  }

  const body = (await response.json()) as ApiCatalogDetailResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to load API request detail');
  }
  return body;
};

const fetchApiRequestOccurrence = async (
  requestId: string,
  occurrenceId: string
): Promise<ApiCatalogOccurrenceResponse> => {
  const response = await fetch(`${API_URL}/api-requests/${requestId}/occurrences/${occurrenceId}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to load API request occurrence');
  }

  const body = (await response.json()) as ApiCatalogOccurrenceResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to load API request occurrence');
  }
  return body;
};

const apiCatalogKeys = {
  all: ['api-catalog'] as const,
  list: (params: ApiRequestListQuery) =>
    [
      ...apiCatalogKeys.all,
      'list',
      params.page ?? 1,
      params.pageSize ?? 25,
      params.search ?? '',
      params.method ?? '',
      (params.methods ?? []).join(','),
      params.propertyId ?? '',
      (params.domains ?? []).join(','),
      params.status ?? '',
      (params.statuses ?? []).join(','),
      (params.credentialIds ?? []).join(','),
      params.liveness ?? '',
      params.lastSeenStart ?? '',
      params.lastSeenEnd ?? '',
    ] as const,
  detail: (requestId: string) => [...apiCatalogKeys.all, 'detail', requestId] as const,
  occurrence: (requestId: string, occurrenceId: string) =>
    [...apiCatalogKeys.all, 'occurrence', requestId, occurrenceId] as const,
  credentials: (requestId: string) => [...apiCatalogKeys.all, 'credentials', requestId] as const,
  domains: ['api-catalog', 'domains'] as const,
};

export const useApiRequestList = (
  params: ApiRequestListQuery & { enabled?: boolean }
): UseQueryResult<ApiCatalogListResponse> => {
  const { enabled = true, ...queryParams } = params;
  return useQuery<ApiCatalogListResponse>({
    queryKey: apiCatalogKeys.list(queryParams),
    queryFn: () => fetchApiRequestList(queryParams),
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    enabled,
  });
};

export const useApiRequestDetail = (
  requestId: string | null
): UseQueryResult<ApiCatalogDetailResponse> => {
  return useQuery<ApiCatalogDetailResponse>({
    queryKey: requestId ? apiCatalogKeys.detail(requestId) : apiCatalogKeys.detail(''),
    queryFn: () => {
      if (!requestId) {
        throw new Error('Missing request id');
      }
      return fetchApiRequestDetail(requestId);
    },
    enabled: !!requestId,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
};

export const useApiRequestOccurrence = (
  requestId: string | null,
  occurrenceId: string | null,
  enabled: boolean
): UseQueryResult<ApiCatalogOccurrenceResponse> => {
  return useQuery<ApiCatalogOccurrenceResponse>({
    queryKey:
      requestId && occurrenceId
        ? apiCatalogKeys.occurrence(requestId, occurrenceId)
        : apiCatalogKeys.occurrence('', ''),
    queryFn: () => {
      if (!requestId || !occurrenceId) {
        throw new Error('Missing request or occurrence id');
      }
      return fetchApiRequestOccurrence(requestId, occurrenceId);
    },
    enabled: enabled && !!requestId && !!occurrenceId,
  });
};

const fetchApiRequestCredentials = async (
  requestId: string
): Promise<ApiCatalogCredentialsResponse> => {
  const response = await fetch(`${API_URL}/api-requests/${requestId}/credentials`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to load API request credentials: ${response.status}`);
  }

  const body = (await response.json()) as ApiCatalogCredentialsResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to load API request credentials');
  }
  return body;
};

export const useApiRequestCredentials = (
  requestId: string | null
): UseQueryResult<ApiCatalogCredentialsResponse> => {
  return useQuery<ApiCatalogCredentialsResponse>({
    queryKey: requestId ? apiCatalogKeys.credentials(requestId) : apiCatalogKeys.credentials(''),
    queryFn: () => {
      if (!requestId) {
        throw new Error('Missing request id');
      }
      return fetchApiRequestCredentials(requestId);
    },
    enabled: !!requestId,
    staleTime: 30_000,
  });
};

const fetchApiCatalogDomainMetadata = async (): Promise<ApiCatalogDomainMetadataResponse> => {
  const response = await fetch(`${API_URL}/api-requests/domains`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to load API catalog domains: ${response.status}`);
  }

  const body = (await response.json()) as ApiCatalogDomainMetadataResponse;
  if (!body || !body.ok) {
    throw new Error('Failed to load API catalog domains');
  }

  return body;
};

export const useApiCatalogDomainMetadata = (): UseQueryResult<ApiCatalogDomainMetadataResponse> => {
  return useQuery<ApiCatalogDomainMetadataResponse>({
    queryKey: apiCatalogKeys.domains,
    queryFn: fetchApiCatalogDomainMetadata,
    staleTime: 5 * 60_000,
  });
};

type ExportToPostmanOptions = {
  domain: string;
  includeExamples?: boolean;
};

const exportDomainToPostman = async (options: ExportToPostmanOptions): Promise<void> => {
  const { domain, includeExamples = true } = options;

  const params = new URLSearchParams({
    domain,
    includeExamples: String(includeExamples),
  });

  const response = await fetch(`${API_URL}/api-requests/export?${params}`, {
    credentials: 'include',
  });

  if (response.status === 404) {
    throw new Error(`No endpoints found for domain: ${domain}`);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Export failed: ${response.status}`);
  }

  // Get filename from Content-Disposition header or generate one
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `${domain}.postman_collection.json`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match) {
      filename = match[1];
    }
  }

  // Download the file
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const useExportToPostman = () => {
  return useMutation({
    mutationFn: exportDomainToPostman,
  });
};

export type PostmanCollectionVariable = {
  key: string;
  value: string;
};

export type VariableOverride = {
  value: string;
  mode: 'hardcode' | 'parameter';
};

type ImportPreviewResult = {
  ok: boolean;
  data?: { variables: PostmanCollectionVariable[] };
  error?: string;
};

type ImportPostmanResult = {
  ok: boolean;
  data?: { imported: number; skipped: number; total: number };
  error?: string;
};

const previewPostmanImport = async (file: File): Promise<ImportPreviewResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_URL}/api-requests/import/preview`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  const body = (await response.json()) as ImportPreviewResult;
  if (!response.ok || !body.ok) {
    throw new ApiError({
      message: body.error ?? `Preview failed: ${response.status}`,
      status: response.status,
      userError: true,
    });
  }
  return body;
};

const importPostmanCollection = async (opts: {
  file: File;
  propertyId: string;
  variableOverrides?: Record<string, VariableOverride>;
}): Promise<ImportPostmanResult> => {
  const formData = new FormData();
  formData.append('file', opts.file);
  formData.append('propertyId', opts.propertyId);
  if (opts.variableOverrides) {
    formData.append('variableOverrides', JSON.stringify(opts.variableOverrides));
  }

  const response = await fetch(`${API_URL}/api-requests/import`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  const body = (await response.json()) as ImportPostmanResult;
  if (!response.ok || !body.ok) {
    throw new ApiError({
      message: body.error ?? `Import failed: ${response.status}`,
      status: response.status,
      userError: true,
    });
  }
  return body;
};

export const usePreviewPostmanImport = () => {
  return useMutation({
    mutationFn: previewPostmanImport,
  });
};

export const useImportPostmanCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: importPostmanCollection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.all });
    },
  });
};

type ExecuteApiTestOptions = {
  requestId: string;
  options: ApiTestRequest;
};

const executeApiTest = async (
  params: ExecuteApiTestOptions
): Promise<ApiTestResponse | ApiTestErrorResponse> => {
  const response = await fetch(`${API_URL}/api-requests/${params.requestId}/test`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(params.options),
  });

  const body = (await response.json()) as ApiTestResponse | ApiTestErrorResponse;
  return body;
};

export const useExecuteApiTest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: executeApiTest,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.detail(variables.requestId) });
    },
  });
};

// --- Create / Update / Test URL ---

export type ApiCatalogFetch = (
  input: Parameters<typeof fetch>[0],
  init?: Parameters<typeof fetch>[1]
) => ReturnType<typeof fetch>;

type ApiCatalogRequestOptions = {
  apiUrl?: string;
  fetchFn?: ApiCatalogFetch;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const createApiRequestWithOptions = async (
  params: ApiCatalogCreateRequest,
  { apiUrl = API_URL, fetchFn = fetch }: ApiCatalogRequestOptions = {}
): Promise<ApiCatalogMutationResponse> => {
  const response = await fetchFn(`${apiUrl}/api-requests`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to create manual API request');
  }

  const body = (await response.json()) as unknown;
  if (!isRecord(body) || body.ok !== true) {
    const errorBody = isRecord(body) ? body : {};
    throw new ApiError({
      message:
        typeof errorBody.error === 'string' ? errorBody.error : `Create failed: ${response.status}`,
      status: response.status,
      userError: errorBody.userError === true,
      code: typeof errorBody.code === 'string' ? errorBody.code : undefined,
      details: errorBody,
    });
  }
  return body as ApiCatalogMutationResponse;
};

const createApiRequest = async (
  params: ApiCatalogCreateRequest
): Promise<ApiCatalogMutationResponse> => createApiRequestWithOptions(params);

export const useCreateApiRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createApiRequest,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: apiCatalogKeys.all });
    },
  });
};

const updateApiRequest = async (
  params: {
    requestId: string;
  } & ApiCatalogUpdateRequest
): Promise<ApiCatalogMutationResponse> => {
  const { requestId, ...body } = params;
  const response = await fetch(`${API_URL}/api-requests/${requestId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  const result = await response.json();
  if (!response.ok || !result.ok) {
    throw new Error(result.error ?? `Update failed: ${response.status}`);
  }
  return result as ApiCatalogMutationResponse;
};

export const useUpdateApiRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateApiRequest,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.all });
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.detail(variables.requestId) });
    },
  });
};

const bulkDeleteApiRequests = async (ids: string[]): Promise<{ ok: boolean; deleted: number }> => {
  const response = await fetch(`${API_URL}/api-requests/bulk`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    const result = (await response.json()) as { error?: string };
    throw new Error(result.error ?? `Bulk delete failed: ${response.status}`);
  }

  return response.json() as Promise<{ ok: boolean; deleted: number }>;
};

export const useBulkDeleteApiRequests = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkDeleteApiRequests,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.all });
    },
  });
};

const testApiUrl = async (
  params: ApiTestUrlRequest
): Promise<ApiTestUrlResponse | ApiTestErrorResponse> => {
  const response = await fetch(`${API_URL}/api-requests/test-url`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params),
  });

  return response.json() as Promise<ApiTestUrlResponse | ApiTestErrorResponse>;
};

export const useTestApiUrl = () => {
  return useMutation({
    mutationFn: testApiUrl,
  });
};

// ---- API Catalog Sources ----

const sourceKeys = {
  all: ['api-catalog-sources'] as const,
  list: ['api-catalog-sources', 'list'] as const,
  detail: (id: string) => ['api-catalog-sources', 'detail', id] as const,
};

const previewUrlSource = async (url: string): Promise<ApiCatalogSourcePreviewResponse> => {
  const response = await fetch(`${API_URL}/api-catalog-sources/preview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ url }),
  });

  const body = (await response.json()) as ApiCatalogSourcePreviewResponse;
  if (!response.ok || !body.ok) {
    throw new Error(body.error ?? `Preview failed: ${response.status}`);
  }
  return body;
};

export const usePreviewUrlSource = () => {
  return useMutation({
    mutationFn: previewUrlSource,
  });
};

const createSource = async (
  params: ApiCatalogSourceCreateRequest
): Promise<ApiCatalogSourceCreateResponse> => {
  const response = await fetch(`${API_URL}/api-catalog-sources`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params),
  });

  const body = (await response.json()) as ApiCatalogSourceCreateResponse;
  if (!response.ok || !body.ok) {
    throw new Error(body.error ?? `Create failed: ${response.status}`);
  }
  return body;
};

export const useCreateSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.all });
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.all });
    },
  });
};

const fetchSources = async (): Promise<ApiCatalogSource[]> => {
  const response = await fetch(`${API_URL}/api-catalog-sources`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to load sources: ${response.status}`);
  }

  const body = (await response.json()) as ApiCatalogSourceListResponse;
  if (!body.ok) {
    throw new Error('Failed to load sources');
  }
  return body.data;
};

export const useListSources = () => {
  return useQuery<ApiCatalogSource[]>({
    queryKey: sourceKeys.list,
    queryFn: fetchSources,
    staleTime: 30_000,
  });
};

const refreshSourceApi = async (
  sourceId: string
): Promise<ApiCatalogSourceRefreshResponse> => {
  const response = await fetch(`${API_URL}/api-catalog-sources/${sourceId}/refresh`, {
    method: 'POST',
    credentials: 'include',
  });

  const body = (await response.json()) as ApiCatalogSourceRefreshResponse;
  if (!response.ok || !body.ok) {
    throw new Error(body.error ?? `Refresh failed: ${response.status}`);
  }
  return body;
};

export const useRefreshSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refreshSourceApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.all });
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.all });
    },
  });
};

const deleteSourceApi = async (
  sourceId: string
): Promise<{ ok: boolean; data?: { deletedEndpoints: number } }> => {
  const response = await fetch(`${API_URL}/api-catalog-sources/${sourceId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  const body = (await response.json()) as { ok: boolean; error?: string; data?: { deletedEndpoints: number } };
  if (!response.ok || !body.ok) {
    throw new Error(body.error ?? `Delete failed: ${response.status}`);
  }
  return body;
};

export const useDeleteSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSourceApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sourceKeys.all });
      queryClient.invalidateQueries({ queryKey: apiCatalogKeys.all });
    },
  });
};
